import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, PackageCheck, Truck, AlertTriangle, Replace, Undo2 } from "lucide-react";
import SubstitutePicker from "@/components/SubstitutePicker";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

/**
 * Receive-against-PO — the packing-slip check-off dialog. Opened from
 * the Open POs panel on the Receive Dashboard. Each PO line pre-fills
 * with the ordered qty and unit-cost snapshot; the receiver ticks each
 * one they can see in the box, edits qty if short-delivered, and hits
 * Save. The backend writes one IN movement per ticked line via the
 * shared movement writer so Reports / History / Spend stay consistent.
 *
 * Short-delivered lines (qty received < qty ordered) stay outstanding
 * — the PO moves to `partially_received` and comes back into the Open
 * POs panel next time.
 */
const ReceiveFromPODialog = ({ open, po, onClose, onReceived }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !po) return;
    // Each row starts pre-checked at outstanding qty (ordered − already
    // received). Only lines with outstanding > 0 are eligible.
    const initial = (po.items || []).map((it) => {
      const outstanding = Math.max(
        0,
        Number(it.qty || 0) - Number(it.qty_received || 0),
      );
      return {
        stock_id: it.stock_id,
        stock_name: it.stock_name,
        sku: it.sku,
        unit: it.unit,
        ordered_qty: Number(it.qty || 0),
        already_received: Number(it.qty_received || 0),
        outstanding,
        qty: outstanding,
        unit_cost: Number(it.unit_cost || 0),
        include_processor_fee: !!po.companion_id, // auto-check when there's a companion (v171 roll-in)
        batch_ref: "",
        notes: "",
        checked: outstanding > 0,
        orig_unit_cost: Number(it.unit_cost || 0),
        sub: null,
        discontinue: false,
        picking: false,
      };
    });
    setRows(initial);
  }, [open, po]);

  const patch = (i, k, v) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const patchMany = (i, obj) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...obj } : r)));
  const pickSub = (i, s) =>
    patchMany(i, {
      sub: { id: s.id, name: s.name, unit: s.unit },
      unit_cost: Number(s.unit_cost || 0),
      picking: false,
      checked: true,
    });
  const clearSub = (i) =>
    setRows((rs) =>
      rs.map((r, idx) =>
        idx === i ? { ...r, sub: null, discontinue: false, unit_cost: r.orig_unit_cost } : r,
      ),
    );

  const totals = useMemo(
    () => ({
      picked: rows.filter((r) => r.checked && Number(r.qty) > 0).length,
      value: rows
        .filter((r) => r.checked)
        .reduce((s, r) => s + Number(r.qty || 0) * Number(r.unit_cost || 0), 0),
    }),
    [rows],
  );

  const outstandingAfter = useMemo(
    () =>
      rows.filter(
        (r) => !r.checked || Number(r.qty || 0) < r.outstanding,
      ).length,
    [rows],
  );

  const submit = async () => {
    const lines = rows
      .filter((r) => r.checked && Number(r.qty) > 0)
      .map((r) => ({
        stock_id: r.stock_id,
        qty: Number(r.qty),
        unit_cost: Number(r.unit_cost),
        include_processor_fee: r.include_processor_fee,
        batch_ref: (r.batch_ref || "").trim() || undefined,
        notes: (r.notes || "").trim() || undefined,
        substitute_stock_id: r.sub?.id || undefined,
        discontinue_original: r.sub ? r.discontinue : undefined,
      }));
    if (!lines.length) return toast.error("Tick at least one line to receive");
    if (outstandingAfter > 0) {
      const ok = window.confirm(
        `${outstandingAfter} line${outstandingAfter === 1 ? "" : "s"} will stay outstanding on this PO. Continue?`,
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/po/${po.id}/receive-lines`, { lines });
      const short = data.po.status === "partially_received";
      toast.success(
        short
          ? `${lines.length} line(s) received · ${po.po_number} → Partially received`
          : `${lines.length} line(s) received · ${po.po_number} complete`,
        { duration: 5000 },
      );
      window.dispatchEvent(new Event("stock-updated"));
      onReceived?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not receive lines");
    } finally {
      setBusy(false);
    }
  };

  if (!open || !po) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="receive-from-po-overlay"
    >
      <div
        className="nb-card bg-white max-w-4xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="receive-from-po-dialog"
      >
        <div className="flex items-start justify-between p-5 border-b-2 border-black gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow flex items-center gap-2">
              <Truck size={12} strokeWidth={2.5} /> Receive against PO
            </div>
            <h3 className="font-display text-2xl leading-tight mt-1">
              {po.po_number}
              <span className="text-sm font-normal text-[#607087] ml-2">
                {po.supplier_snapshot?.name || ""}
              </span>
            </h3>
            <div className="text-xs text-[#607087] mt-1">
              Tick each line as you check the packing slip. Edit qty if
              short-delivered — outstanding lines stay on this PO.
            </div>
          </div>
          <button
            data-testid="receive-from-po-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
            aria-label="close"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-2 max-h-[65vh] overflow-y-auto">
          {rows.map((r, i) => {
            const shortDelivery = r.checked && Number(r.qty) < r.outstanding;
            return (
              <div
                key={r.stock_id}
                data-testid={`receive-from-po-line-${i}`}
                className={`border-2 rounded-xl p-3 flex flex-wrap items-center gap-2 transition-all ${
                  !r.checked
                    ? "border-black/30 bg-[#FAFBFC] opacity-70"
                    : shortDelivery
                      ? "border-[#7A5A00] bg-[#FFF8E1]"
                      : "border-black bg-white"
                }`}
              >
                <label className="flex items-center gap-2 min-w-0 flex-1">
                  <input
                    type="checkbox"
                    data-testid={`receive-from-po-check-${i}`}
                    checked={r.checked}
                    onChange={(e) => patch(i, "checked", e.target.checked)}
                    disabled={r.outstanding <= 0}
                    className="w-5 h-5"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-base truncate">
                      {r.stock_name}
                      {r.sku && (
                        <span className="text-[10px] text-[#607087] font-mono-num ml-1">
                          {r.sku}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#607087]">
                      Ordered <b className="font-mono-num">{r.ordered_qty}</b>{" "}
                      {r.unit}
                      {r.already_received > 0 && (
                        <>
                          {" · already received "}
                          <b className="font-mono-num">{r.already_received}</b>
                        </>
                      )}
                      {r.outstanding > 0 && (
                        <>
                          {" · outstanding "}
                          <b className="font-mono-num text-[#8A1F1F]">
                            {r.outstanding}
                          </b>
                        </>
                      )}
                      {r.outstanding <= 0 && (
                        <span className="ml-1 text-[#1F6F3E] font-bold">✓ complete</span>
                      )}
                    </div>
                  </div>
                </label>
                {r.outstanding > 0 && !r.sub && (
                  <button
                    type="button"
                    data-testid={`receive-from-po-sub-btn-${i}`}
                    onClick={() => patch(i, "picking", !r.picking)}
                    className="nb-btn bg-white px-2.5 py-1.5 text-xs flex items-center gap-1"
                    title="Supplier sent a different item in place of this one"
                  >
                    <Replace size={12} strokeWidth={2.5} /> Substitute
                  </button>
                )}
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid={`receive-from-po-qty-${i}`}
                    className="nb-input py-1.5 w-20 text-center font-mono-num text-sm"
                    value={r.qty}
                    onChange={(e) => patch(i, "qty", e.target.value)}
                    disabled={!r.checked || r.outstanding <= 0}
                  />
                  <span className="text-[11px] text-[#607087]">{r.sub?.unit || r.unit}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-[#607087]">@</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid={`receive-from-po-cost-${i}`}
                    className="nb-input py-1.5 w-24 text-right font-mono-num text-sm"
                    value={r.unit_cost}
                    onChange={(e) => patch(i, "unit_cost", e.target.value)}
                    disabled={!r.checked}
                  />
                </div>
                {po.companion_id && (
                  <label
                    className="flex items-center gap-1 text-[11px] text-[#7A5300] font-bold"
                    title="Roll in the toll-processing fee from the linked companion PO (v171)"
                  >
                    <input
                      type="checkbox"
                      checked={r.include_processor_fee}
                      onChange={(e) =>
                        patch(i, "include_processor_fee", e.target.checked)
                      }
                      disabled={!r.checked}
                      className="w-4 h-4"
                    />
                    +fee
                  </label>
                )}
                {r.picking && !r.sub && (
                  <SubstitutePicker
                    original={r}
                    onPick={(s) => pickSub(i, s)}
                    onClose={() => patch(i, "picking", false)}
                  />
                )}
                {r.sub && (
                  <div
                    data-testid={`receive-from-po-sub-chip-${i}`}
                    className="basis-full flex flex-wrap items-center gap-2 border-2 border-[#0A3A7A] bg-[#EAF5FB] rounded-lg px-2.5 py-1.5 text-xs"
                  >
                    <Replace size={12} strokeWidth={2.5} className="text-[#0A3A7A]" />
                    <span>
                      Receiving <b data-testid={`receive-from-po-sub-name-${i}`}>{r.sub.name}</b> as a substitute
                      — fills this PO line
                    </span>
                    <label className="flex items-center gap-1 font-bold text-[#7A5300]">
                      <input
                        type="checkbox"
                        data-testid={`receive-from-po-discontinue-${i}`}
                        checked={r.discontinue}
                        onChange={(e) => patch(i, "discontinue", e.target.checked)}
                        className="w-4 h-4"
                      />
                      Original discontinued — use substitute on future reorders
                    </label>
                    <button
                      type="button"
                      data-testid={`receive-from-po-sub-undo-${i}`}
                      onClick={() => clearSub(i)}
                      className="nb-btn bg-white px-2 py-1 text-[11px] flex items-center gap-1 ml-auto"
                    >
                      <Undo2 size={11} strokeWidth={2.5} /> Undo
                    </button>
                  </div>
                )}
                {shortDelivery && (
                  <div className="basis-full text-[11px] text-[#7A5300] flex items-center gap-1">
                    <AlertTriangle size={11} strokeWidth={2.5} />
                    Short delivery — {r.outstanding - Number(r.qty)} {r.unit} stays outstanding
                  </div>
                )}
                {/* v204 — Per-line Certificate-of-Compliance / batch ref +
                    notes. Lives on the receive-against-PO dialog (was
                    missing — manual Take Stock had it but PO-driven
                    receive didn't). Captured on the resulting IN
                    movement and surfaced in the Item History + Reports
                    movement drill-ins for traceability when a bad
                    powder batch is discovered later. */}
                {r.checked && (
                  <div className="basis-full flex flex-col sm:flex-row gap-2 pt-1 border-t border-black/10 mt-1">
                    <label className="flex-1 min-w-0">
                      <div className="label-eyebrow mb-1 flex items-center gap-1">
                        CoC / Batch ref
                        <span className="text-[9px] text-[#607087] font-normal normal-case">
                          — for traceability if a bad batch is reported later
                        </span>
                      </div>
                      <input
                        type="text"
                        data-testid={`receive-from-po-batch-${i}`}
                        className="nb-input py-1.5 text-sm font-mono-num"
                        value={r.batch_ref}
                        onChange={(e) => patch(i, "batch_ref", e.target.value)}
                        placeholder="e.g. CoC-2026-PN11-4421"
                      />
                    </label>
                    <label className="flex-1 min-w-0">
                      <div className="label-eyebrow mb-1">Notes</div>
                      <input
                        type="text"
                        data-testid={`receive-from-po-notes-${i}`}
                        className="nb-input py-1.5 text-sm"
                        value={r.notes}
                        onChange={(e) => patch(i, "notes", e.target.value)}
                        placeholder="Pallet 3 damaged, decanted on arrival…"
                      />
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="p-5 border-t-2 border-black flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm">
            <b className="font-mono-num">{totals.picked}</b> line
            {totals.picked === 1 ? "" : "s"} to receive ·{" "}
            <b className="font-mono-num">{money(totals.value)}</b>
            {outstandingAfter > 0 && (
              <span className="text-[11px] text-[#7A5300] ml-2 font-bold">
                · {outstandingAfter} line{outstandingAfter === 1 ? "" : "s"} will stay outstanding
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              data-testid="receive-from-po-cancel"
              onClick={onClose}
              disabled={busy}
              className="nb-btn bg-white px-4 py-2"
            >
              Cancel
            </button>
            <button
              data-testid="receive-from-po-save"
              onClick={submit}
              disabled={busy || totals.picked === 0}
              className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-1.5"
            >
              <PackageCheck size={14} strokeWidth={2.5} />
              {busy ? "Saving…" : "Receive"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiveFromPODialog;
